import { useToken } from '@ori/auth';
import { axios } from '@ori/axios';
import { useGetApplicationsNavigationCustomerInfo } from '@ori/global-api-client-react';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { VipAccessResponse } from '../models';
import { vipOrderUrls } from '../utils';
import { useAppConfig } from './configurationContext';

export interface ConsultantContext {
  consultantNumber?: string | undefined;
  consultantType?: number | undefined;
  isOrderOnBehalfVisible?: boolean;
  loadingVipAccess: boolean;
  vipAccess?: VipAccessResponse;
}

export const consultantContext = createContext<ConsultantContext>({
  loadingVipAccess: true,
});

const { Provider } = consultantContext;

export const ConsultantProvider: FC<PropsWithChildren> = ({ children }) => {
  const [vipAccess, setVipAccess] = useState<VipAccessResponse>();
  const [loadingVipAccess, setLoadingVipAccess] = useState(true);
  const { consultantNumber, consultantType } = useAppConfig();
  const { customerId, isAnonymousUser } = useToken();
  const { data } = useGetApplicationsNavigationCustomerInfo(customerId.toString(), undefined, {
    swr: { enabled: !isAnonymousUser },
  });
  useEffect(() => {
    const abortController = new AbortController();
    axios
      .get<VipAccessResponse>(vipOrderUrls.access, {
        baseURL: window.location.origin,
        signal: abortController.signal,
      })
      .then((response) => setVipAccess(response.data))
      // eslint-disable-next-line no-console -- log error to console
      .catch(console.error)
      .finally(() => setLoadingVipAccess(false));

    return () => abortController.abort();
  }, []);
  const value = useMemo<ConsultantContext>(
    () => ({
      consultantNumber: data?.consultantNumber?.toString() ?? consultantNumber,
      consultantType,
      isOrderOnBehalfVisible: !!(vipAccess?.VipAccessData?.ConsultantName && vipAccess.VipAccessData.ConsultantNumber),
      loadingVipAccess,
      vipAccess,
    }),
    [consultantNumber, consultantType, data?.consultantNumber, loadingVipAccess, vipAccess],
  );

  return <Provider value={value}>{children}</Provider>;
};

export const useConsultant = () => useContext(consultantContext);
